/**
 * File Manager Provider Component
 *
 * Sets up auto-save and unsaved changes warning for the application.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  startAutoSave,
  stopAutoSave,
  setupUnsavedChangesWarning,
  useFileStore,
} from '../index';
import { useIDFStore } from '../../stores/idfStore';
import { ImportDialog } from './ImportDialog';
import { ExportDialog } from './ExportDialog';
import { ExampleModelsDialog } from './ExampleModelsDialog';
import { SavedModelsDialog } from './SavedModelsDialog';

interface FileManagerProviderProps {
  children: React.ReactNode;
}

export function FileManagerProvider({ children }: FileManagerProviderProps) {
  const navigate = useNavigate();
  const {
    importDialogOpen,
    setImportDialogOpen,
    exportDialogOpen,
    setExportDialogOpen,
    exampleModelsDialogOpen,
    setExampleModelsDialogOpen,
    savedModelsDialogOpen,
    setSavedModelsDialogOpen,
    markDirty,
    shouldNavigateToEditor,
    clearNavigationSignal,
  } = useFileStore();

  const isDirty = useIDFStore((state) => state.isDirty);

  // Handle navigation signal from store (for home page model loading)
  useEffect(() => {
    if (shouldNavigateToEditor) {
      clearNavigationSignal();
      void navigate('/editor');
    }
  }, [shouldNavigateToEditor, clearNavigationSignal, navigate]);

  // Start auto-save on mount
  useEffect(() => {
    startAutoSave();
    const cleanup = setupUnsavedChangesWarning();

    return () => {
      stopAutoSave();
      cleanup();
    };
  }, []);

  // Sync dirty state from IDF store to file store
  useEffect(() => {
    if (isDirty) {
      markDirty();
    }
  }, [isDirty, markDirty]);

  // Setup keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + S: Save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        void useFileStore.getState().saveModel();
      }

      // Ctrl/Cmd + O: Import
      if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
        e.preventDefault();
        setImportDialogOpen(true);
      }

      // Ctrl/Cmd + E: Export
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        setExportDialogOpen(true);
      }

      // Ctrl/Cmd + N: New
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        const store = useFileStore.getState();
        if (store.hasUnsavedChanges) {
          if (confirm('You have unsaved changes. Create a new document anyway?')) {
            store.newDocument();
          }
        } else {
          store.newDocument();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); };
  }, [setImportDialogOpen, setExportDialogOpen]);

  return (
    <>
      {children}

      {/* Dialogs */}
      <ImportDialog open={importDialogOpen} onOpenChange={setImportDialogOpen} />
      <ExportDialog open={exportDialogOpen} onOpenChange={setExportDialogOpen} />
      <ExampleModelsDialog
        open={exampleModelsDialogOpen}
        onOpenChange={setExampleModelsDialogOpen}
      />
      <SavedModelsDialog
        open={savedModelsDialogOpen}
        onOpenChange={setSavedModelsDialogOpen}
      />
    </>
  );
}
